import { useRef, useState } from 'react';
import Header from '../components/Header';
import { submitOnboardingForm } from '../services/onboardingService';

interface OnboardingFormData {
  // Section 1: Garage Owner Details
  fullName: string;
  contactEmail: string;
  phoneNumber: string;
  businessName: string;
  
  // Section 2: Garage Location & Identity
  garageName: string;
  fullAddress: string;
  googleMapsLink: string;
  garagePhoto: File | null;
  lotSignagePhoto: File | null;
  locationType: string;
  parkingType: string;
  
  // Section 3: Parking Capacity & Rates
  totalSpaces: number;
  spacesForParkRez: number;
  ratePerHour: number;
  dailyMaxRate: number;
  operatingHours: string;
  
  // Section 4: Security & Restrictions
  securityType: string;
  accessMethod: string;
  restrictions: string;
  specialInstructions: string;
  amenities: string;

  allowOvernightParking: boolean;
  
  // Section 5: Legal & Payout Info
  businessTaxId: string;
  payoutEmail: string;
  agreeToTerms: boolean;
  signature: string;
  signatureDate: string;
}

type FormStep = 'owner-details' | 'location-identity' | 'capacity-rates' | 'security-restrictions' | 'legal-payout' | 'review';

const OnboardingPage = () => {
  const [formData, setFormData] = useState<OnboardingFormData>({
    // Section 1
    fullName: '',
    contactEmail: '',
    phoneNumber: '',
    businessName: '',
    
    // Section 2
    garageName: '',
    fullAddress: '',
    googleMapsLink: '',
    garagePhoto: null,
    lotSignagePhoto: null,
    locationType: '',
    parkingType: '',
    
    // Section 3
    totalSpaces: 0,
    spacesForParkRez: 0,
    ratePerHour: 0,
    dailyMaxRate: 0,
    operatingHours: '',
    
    // Section 4
    securityType: '',
    accessMethod: '',
    restrictions: '',
    specialInstructions: '',
    amenities: '',

    allowOvernightParking: false,
    
    // Section 5
    businessTaxId: '',
    payoutEmail: '',
    agreeToTerms: false,
    signature: '',
    signatureDate: ''
  });

  const [currentStep, setCurrentStep] = useState<FormStep>('owner-details');
  const [errors, setErrors] = useState<Partial<Record<keyof OnboardingFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [garageImagePreview, setGarageImagePreview] = useState<string | null>(null);
  const [signageImagePreview, setSignageImagePreview] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const garagePhotoRef = useRef<HTMLInputElement>(null);
  const signagePhotoRef = useRef<HTMLInputElement>(null);

  const steps: { key: FormStep; title: string; description: string }[] = [
    { key: 'owner-details', title: 'Owner Details', description: 'Contact information' },
    { key: 'location-identity', title: 'Location & Identity', description: 'Garage details & photos' },
    { key: 'capacity-rates', title: 'Capacity & Rates', description: 'Spaces & pricing' },
    { key: 'security-restrictions', title: 'Security & Restrictions', description: 'Access & rules' },
    { key: 'legal-payout', title: 'Legal & Payout', description: 'Terms & payment info' },
    { key: 'review', title: 'Review & Submit', description: 'Final confirmation' }
  ];

  const currentStepIndex = steps.findIndex(step => step.key === currentStep);

  const locationTypes = [
    { value: 'garage', label: 'Garage (indoor structure)', tooltip: 'Indoor or multi-level structure' },
    { value: 'lot', label: 'Lot (open-air surface lot)', tooltip: 'Outdoor surface lot' },
    { value: 'private', label: 'Private Space', tooltip: 'Driveway, church, residential or unlisted space' }
  ];

  const parkingTypes = [
    { value: 'self', label: 'Self Park', tooltip: 'Drivers park their own vehicles' },
    { value: 'valet', label: 'Valet', tooltip: 'Professional attendant parks vehicles' }
  ];
  const securityTypes = ['Cameras', 'Attendant', 'Gate', 'None'];
  const accessMethods = ['Open', 'Code Gate', 'Staffed'];

  const validateStep = (step: FormStep): boolean => {
    const newErrors: Partial<Record<keyof OnboardingFormData, string>> = {};

    if (step === 'owner-details') {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!formData.contactEmail.trim()) {
        newErrors.contactEmail = 'Contact email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
        newErrors.contactEmail = 'Please enter a valid email address';
      }
      // Phone number is optional
    }

    if (step === 'location-identity') {
      if (!formData.garageName.trim()) newErrors.garageName = 'Garage name is required';
      if (!formData.fullAddress.trim()) newErrors.fullAddress = 'Full address is required';
      if (!formData.locationType) newErrors.locationType = 'Location type is required';
      if (!formData.parkingType) newErrors.parkingType = 'Parking type is required';
      if (!formData.garagePhoto) newErrors.garagePhoto = 'Garage photo is required';
    }

    if (step === 'capacity-rates') {
      if (!formData.totalSpaces || formData.totalSpaces <= 0) newErrors.totalSpaces = 'Total spaces must be greater than 0';
      if (!formData.spacesForParkRez || formData.spacesForParkRez <= 0) newErrors.spacesForParkRez = 'Spaces for ParkRez must be greater than 0';
      if (!formData.ratePerHour || formData.ratePerHour <= 0) newErrors.ratePerHour = 'Rate per hour must be greater than 0';
      if (!formData.operatingHours.trim()) newErrors.operatingHours = 'Operating hours are required';
    }

    if (step === 'security-restrictions') {
      // Security restrictions step is optional - no validation required
      // Users can proceed without filling out security details
    }

    if (step === 'legal-payout') {
      if (!formData.payoutEmail.trim()) {
        newErrors.payoutEmail = 'Payout email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.payoutEmail)) {
        newErrors.payoutEmail = 'Please enter a valid email address';
      }
      if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to terms and conditions';
      if (!formData.signature.trim()) newErrors.signature = 'Signature is required';
    }

    if (step === 'review') {
      // Review step doesn't need validation - it's just for reviewing
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      const numValue = value === '' ? 0 : parseFloat(value) || 0;
      setFormData(prev => ({ ...prev, [name]: numValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when user starts typing
    if (errors[name as keyof OnboardingFormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'garage' | 'signage') => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, [type === 'garage' ? 'garagePhoto' : 'lotSignagePhoto']: 'Please select an image file' }));
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, [type === 'garage' ? 'garagePhoto' : 'lotSignagePhoto']: 'Image size must be less than 5MB' }));
        return;
      }

      setFormData(prev => ({ 
        ...prev, 
        [type === 'garage' ? 'garagePhoto' : 'lotSignagePhoto']: file 
      }));
      setErrors(prev => ({ ...prev, [type === 'garage' ? 'garagePhoto' : 'lotSignagePhoto']: undefined }));

      const reader = new FileReader();
      reader.onload = (e) => {
        if (type === 'garage') {
          setGarageImagePreview(e.target?.result as string);
        } else {
          setSignageImagePreview(e.target?.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const file = e.dataTransfer.files[0];
    if (file) {
      const input = garagePhotoRef.current;
      if (input) {
        input.files = e.dataTransfer.files;
        handleFileChange({ target: { files: e.dataTransfer.files } } as any, 'garage');
      }
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      const nextStepIndex = currentStepIndex + 1;
      if (nextStepIndex < steps.length) {
        setCurrentStep(steps[nextStepIndex].key);
      }
    }
  };

  const prevStep = () => {
    const prevStepIndex = currentStepIndex - 1;
    if (prevStepIndex >= 0) {
      setCurrentStep(steps[prevStepIndex].key);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🚀 Submit button clicked');
    console.log('Current step:', currentStep);
    console.log('Form data:', formData);
    
    if (!validateStep(currentStep)) {
      console.log('❌ Validation failed');
      return;
    }

    console.log('✅ Validation passed, starting submission...');
    setIsSubmitting(true);
    
    try {
      // Set signature date
      const submissionData = {
        ...formData,
        signatureDate: new Date().toISOString()
      };

      console.log('📤 Calling submitOnboardingForm...');
      const result = await submitOnboardingForm(submissionData, formData.garagePhoto, formData.lotSignagePhoto);
      console.log('✅ submitOnboardingForm completed:', result);
      
      alert(result.message);
      
      // Reset form with correct field names
      setFormData({
        fullName: '',
        contactEmail: '',
        phoneNumber: '',
        businessName: '',
        garageName: '',
        fullAddress: '',
        googleMapsLink: '',
        garagePhoto: null,
        lotSignagePhoto: null,
        locationType: '', // Fixed: was garageType
        parkingType: '',
        totalSpaces: 0,
        spacesForParkRez: 0,
        ratePerHour: 0,
        dailyMaxRate: 0,
        operatingHours: '',
        securityType: '',
        accessMethod: '',
        restrictions: '',
        specialInstructions: '',
        amenities: '', // Added missing field
        allowOvernightParking: false,
        businessTaxId: '',
        payoutEmail: '',
        agreeToTerms: false,
        signature: '',
        signatureDate: ''
      });
      setGarageImagePreview(null);
      setSignageImagePreview(null);
      setErrors({});
      setCurrentStep('owner-details');
      console.log('✅ Form reset completed');
    } catch (error) {
      console.error('❌ Error submitting form:', error);
      alert('There was an error submitting your application. Please try again.');
    } finally {
      console.log('🏁 Setting isSubmitting to false');
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 'owner-details':
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                className={`input-field ${errors.fullName ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Person responsible for the garage"
              />
              {errors.fullName && (
                <p className="mt-1 text-sm text-red-600">{errors.fullName}</p>
              )}
            </div>

            <div>
              <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email *
              </label>
              <input
                type="email"
                id="contactEmail"
                name="contactEmail"
                value={formData.contactEmail}
                onChange={handleInputChange}
                className={`input-field ${errors.contactEmail ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="For communication and support"
              />
              {errors.contactEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.contactEmail}</p>
              )}
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className={`input-field ${errors.phoneNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Optional - for SMS or direct calls"
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
              )}
            </div>

            <div>
              <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                Business Name (if different)
              </label>
              <input
                type="text"
                id="businessName"
                name="businessName"
                value={formData.businessName}
                onChange={handleInputChange}
                className="input-field"
                placeholder="For invoices or Stripe payouts"
              />
            </div>
          </div>
        );

      case 'location-identity':
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="garageName" className="block text-sm font-medium text-gray-700 mb-2">
                Garage Name *
              </label>
              <input
                type="text"
                id="garageName"
                name="garageName"
                value={formData.garageName}
                onChange={handleInputChange}
                className={`input-field ${errors.garageName ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Will be shown in listings"
              />
              {errors.garageName && (
                <p className="mt-1 text-sm text-red-600">{errors.garageName}</p>
              )}
            </div>

            <div>
              <label htmlFor="fullAddress" className="block text-sm font-medium text-gray-700 mb-2">
                Full Address *
              </label>
              <textarea
                id="fullAddress"
                name="fullAddress"
                value={formData.fullAddress}
                onChange={handleInputChange}
                rows={3}
                className={`input-field ${errors.fullAddress ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Used to show in app and verify"
              />
              {errors.fullAddress && (
                <p className="mt-1 text-sm text-red-600">{errors.fullAddress}</p>
              )}
            </div>

            <div>
              <label htmlFor="googleMapsLink" className="block text-sm font-medium text-gray-700 mb-2">
                Google Maps Link
              </label>
              <input
                type="url"
                id="googleMapsLink"
                name="googleMapsLink"
                value={formData.googleMapsLink}
                onChange={handleInputChange}
                className="input-field"
                placeholder="Can auto-generate from address"
              />
            </div>

            <div>
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Parking Type & Location</h3>
                
                <div>
                  <label htmlFor="locationType" className="block text-sm font-medium text-gray-700 mb-2">
                    Where is this parking located? *
                  </label>
                  <select
                    id="locationType"
                    name="locationType"
                    value={formData.locationType}
                    onChange={handleInputChange}
                    className={`input-field ${errors.locationType ? 'border-red-500 focus:ring-red-500' : ''}`}
                  >
                    <option value="">Select location type</option>
                    {locationTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {formData.locationType && (
                    <p className="mt-1 text-sm text-gray-500">
                      {locationTypes.find(t => t.value === formData.locationType)?.tooltip}
                    </p>
                  )}
                  {errors.locationType && (
                    <p className="mt-1 text-sm text-red-600">{errors.locationType}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="parkingType" className="block text-sm font-medium text-gray-700 mb-2">
                    How do drivers park here? *
                  </label>
                  <select
                    id="parkingType"
                    name="parkingType"
                    value={formData.parkingType}
                    onChange={handleInputChange}
                    className={`input-field ${errors.parkingType ? 'border-red-500 focus:ring-red-500' : ''}`}
                  >
                    <option value="">Select parking type</option>
                    {parkingTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  {formData.parkingType && (
                    <p className="mt-1 text-sm text-gray-500">
                      {parkingTypes.find(t => t.value === formData.parkingType)?.tooltip}
                    </p>
                  )}
                  {errors.parkingType && (
                    <p className="mt-1 text-sm text-red-600">{errors.parkingType}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Garage Photo *
              </label>
              <div className="space-y-4">
                {!garageImagePreview ? (
                  <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <div className="space-y-1 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="garagePhoto" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500">
                          <span>Upload a file</span>
                          <input
                            id="garagePhoto"
                            name="garagePhoto"
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(e) => handleFileChange(e, 'garage')}
                            ref={garagePhotoRef}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="relative">
                      <img src={garageImagePreview} alt="Garage preview" className="w-full h-48 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, garagePhoto: null }));
                          setGarageImagePreview(null);
                          if (garagePhotoRef.current) garagePhotoRef.current.value = '';
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
                {errors.garagePhoto && (
                  <p className="mt-1 text-sm text-red-600">{errors.garagePhoto}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Lot Signage / Streetview (if different)
              </label>
              <div className="space-y-4">
                {!signageImagePreview ? (
                  <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-gray-300 rounded-lg">
                    <div className="space-y-1 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                        <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <div className="flex text-sm text-gray-600">
                        <label htmlFor="lotSignagePhoto" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500">
                          <span>Upload a file</span>
                          <input
                            id="lotSignagePhoto"
                            name="lotSignagePhoto"
                            type="file"
                            accept="image/*"
                            className="sr-only"
                            onChange={(e) => handleFileChange(e, 'signage')}
                            ref={signagePhotoRef}
                          />
                        </label>
                        <p className="pl-1">or drag and drop</p>
                      </div>
                      <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="relative">
                      <img src={signageImagePreview} alt="Signage preview" className="w-full h-48 object-cover rounded-lg" />
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, lotSignagePhoto: null }));
                          setSignageImagePreview(null);
                          if (signagePhotoRef.current) signagePhotoRef.current.value = '';
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      case 'capacity-rates':
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="totalSpaces" className="block text-sm font-medium text-gray-700 mb-2">
                Total Garage Capacity (all spots) *
              </label>
              <input
                type="number"
                id="totalSpaces"
                name="totalSpaces"
                value={formData.totalSpaces || ''}
                onChange={handleInputChange}
                min="1"
                className={`input-field ${errors.totalSpaces ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Can limit bookings"
              />
              {errors.totalSpaces && (
                <p className="mt-1 text-sm text-red-600">{errors.totalSpaces}</p>
              )}
            </div>

            <div>
              <label htmlFor="spacesForParkRez" className="block text-sm font-medium text-gray-700 mb-2">
                How many spaces do you want to list on ParkRez? *
              </label>
              <input
                type="number"
                id="spacesForParkRez"
                name="spacesForParkRez"
                value={formData.spacesForParkRez || ''}
                onChange={handleInputChange}
                min="1"
                className={`input-field ${errors.spacesForParkRez ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="If not all are available"
              />
              {errors.spacesForParkRez && (
                <p className="mt-1 text-sm text-red-600">{errors.spacesForParkRez}</p>
              )}
            </div>

            <div>
              <label htmlFor="ratePerHour" className="block text-sm font-medium text-gray-700 mb-2">
                Rate Per Hour *
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  id="ratePerHour"
                  name="ratePerHour"
                  value={formData.ratePerHour || ''}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className={`input-field pl-8 ${errors.ratePerHour ? 'border-red-500 focus:ring-red-500' : ''}`}
                  placeholder="0.00"
                />
              </div>
              {errors.ratePerHour && (
                <p className="mt-1 text-sm text-red-600">{errors.ratePerHour}</p>
              )}
            </div>

            <div>
              <label htmlFor="dailyMaxRate" className="block text-sm font-medium text-gray-700 mb-2">
                Daily Max Rate
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  id="dailyMaxRate"
                  name="dailyMaxRate"
                  value={formData.dailyMaxRate || ''}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="input-field pl-8"
                  placeholder="Useful for full-day parkers"
                />
              </div>
            </div>

            <div>
              <label htmlFor="operatingHours" className="block text-sm font-medium text-gray-700 mb-2">
                Operating Hours *
              </label>
              <input
                type="text"
                id="operatingHours"
                name="operatingHours"
                value={formData.operatingHours}
                onChange={handleInputChange}
                className={`input-field ${errors.operatingHours ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Format: Mon–Fri 8AM–6PM"
              />
              {errors.operatingHours && (
                <p className="mt-1 text-sm text-red-600">{errors.operatingHours}</p>
              )}
            </div>
          </div>
        );

      case 'security-restrictions':
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="securityType" className="block text-sm font-medium text-gray-700 mb-2">
                Security Type
              </label>
              <select
                id="securityType"
                name="securityType"
                value={formData.securityType}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="">Select security type</option>
                {securityTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="accessMethod" className="block text-sm font-medium text-gray-700 mb-2">
                Access Method
              </label>
              <select
                id="accessMethod"
                name="accessMethod"
                value={formData.accessMethod}
                onChange={handleInputChange}
                className="input-field"
              >
                <option value="">Select access method</option>
                {accessMethods.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="restrictions" className="block text-sm font-medium text-gray-700 mb-2">
                Any Restrictions?
              </label>
              <textarea
                id="restrictions"
                name="restrictions"
                value={formData.restrictions}
                onChange={handleInputChange}
                rows={3}
                className="input-field"
                placeholder="E.g., No oversized vehicles, EV only"
              />
            </div>

            <div>
              <label htmlFor="amenities" className="block text-sm font-medium text-gray-700 mb-2">
                Amenities & Special Features
              </label>
              <textarea
                id="amenities"
                name="amenities"
                value={formData.amenities}
                onChange={handleInputChange}
                rows={4}
                className="input-field"
                placeholder="e.g., Security cameras, EV charging, covered parking, 24/7 access, shuttle service, etc."
              />
            </div>

            <div>
              <label htmlFor="specialInstructions" className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information & Special Instructions
              </label>
              <textarea
                id="specialInstructions"
                name="specialInstructions"
                value={formData.specialInstructions}
                onChange={handleInputChange}
                rows={4}
                className="input-field"
                placeholder="Any other details about your parking facility, business hours, special arrangements, or anything else you'd like us to know"
              />
            </div>

            <div className="flex items-center">
              <input
                id="allowOvernightParking"
                name="allowOvernightParking"
                type="checkbox"
                checked={formData.allowOvernightParking}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="allowOvernightParking" className="ml-2 block text-sm text-gray-900">
                Allow Overnight Parking?
              </label>
            </div>
          </div>
        );

      case 'legal-payout':
        return (
          <div className="space-y-6">
            <div>
              <label htmlFor="businessTaxId" className="block text-sm font-medium text-gray-700 mb-2">
                Business Tax ID / EIN
              </label>
              <input
                type="text"
                id="businessTaxId"
                name="businessTaxId"
                value={formData.businessTaxId}
                onChange={handleInputChange}
                className="input-field"
                placeholder="For Stripe Connect payouts"
              />
            </div>

            <div>
              <label htmlFor="payoutEmail" className="block text-sm font-medium text-gray-700 mb-2">
                Payout Email (for Stripe) *
              </label>
              <input
                type="email"
                id="payoutEmail"
                name="payoutEmail"
                value={formData.payoutEmail}
                onChange={handleInputChange}
                className={`input-field ${errors.payoutEmail ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Required for Stripe onboarding"
              />
              {errors.payoutEmail && (
                <p className="mt-1 text-sm text-red-600">{errors.payoutEmail}</p>
              )}
            </div>

            <div className="flex items-start">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
              />
              <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-900">
                I agree to the <a href="/garage-terms.html" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-500">Terms and Conditions</a> *
              </label>
            </div>
            {errors.agreeToTerms && (
              <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms}</p>
            )}

            <div>
              <label htmlFor="signature" className="block text-sm font-medium text-gray-700 mb-2">
                Signature *
              </label>
              <input
                type="text"
                id="signature"
                name="signature"
                value={formData.signature}
                onChange={handleInputChange}
                className={`input-field ${errors.signature ? 'border-red-500 focus:ring-red-500' : ''}`}
                placeholder="Type your full name to sign"
              />
              {errors.signature && (
                <p className="mt-1 text-sm text-red-600">{errors.signature}</p>
              )}
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Review Your Information</h3>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Owner Details</h4>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-gray-500">Full Name:</dt>
                      <dd className="text-gray-900">{formData.fullName}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Contact Email:</dt>
                      <dd className="text-gray-900">{formData.contactEmail}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Phone:</dt>
                      <dd className="text-gray-900">{formData.phoneNumber}</dd>
                    </div>
                    {formData.businessName && (
                      <div>
                        <dt className="text-gray-500">Business Name:</dt>
                        <dd className="text-gray-900">{formData.businessName}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Location & Identity</h4>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-gray-500">Garage Name:</dt>
                      <dd className="text-gray-900">{formData.garageName}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Address:</dt>
                      <dd className="text-gray-900">{formData.fullAddress}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Location Type:</dt>
                      <dd className="text-gray-900">{locationTypes.find(t => t.value === formData.locationType)?.label || formData.locationType}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Parking Type:</dt>
                      <dd className="text-gray-900">{parkingTypes.find(t => t.value === formData.parkingType)?.label || formData.parkingType}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Capacity & Rates</h4>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-gray-500">Total Garage Capacity:</dt>
                      <dd className="text-gray-900">{formData.totalSpaces}</dd>
                    </div>
                    <div>
                                              <dt className="text-gray-500">Spaces Listed on ParkRez:</dt>
                      <dd className="text-gray-900">{formData.spacesForParkRez}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Rate Per Hour:</dt>
                      <dd className="text-gray-900">${formData.ratePerHour}</dd>
                    </div>
                    {formData.dailyMaxRate > 0 && (
                      <div>
                        <dt className="text-gray-500">Daily Max Rate:</dt>
                        <dd className="text-gray-900">${formData.dailyMaxRate}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">Operating Hours:</dt>
                      <dd className="text-gray-900">{formData.operatingHours}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Security & Legal</h4>
                  <dl className="space-y-2 text-sm">
                    {formData.securityType && (
                      <div>
                        <dt className="text-gray-500">Security Type:</dt>
                        <dd className="text-gray-900">{formData.securityType}</dd>
                      </div>
                    )}
                    {formData.accessMethod && (
                      <div>
                        <dt className="text-gray-500">Access Method:</dt>
                        <dd className="text-gray-900">{formData.accessMethod}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="text-gray-500">Overnight Parking:</dt>
                      <dd className="text-gray-900">{formData.allowOvernightParking ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt className="text-gray-500">Payout Email:</dt>
                      <dd className="text-gray-900">{formData.payoutEmail}</dd>
                    </div>
                  </dl>
                </div>
              </div>

              {formData.restrictions && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-2">Restrictions</h4>
                  <p className="text-sm text-gray-700">{formData.restrictions}</p>
                </div>
              )}

              {formData.amenities && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-2">Amenities & Special Features</h4>
                  <p className="text-sm text-gray-700">{formData.amenities}</p>
                </div>
              )}

              {formData.specialInstructions && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-2">Additional Information & Special Instructions</h4>
                  <p className="text-sm text-gray-700">{formData.specialInstructions}</p>
                </div>
              )}

              {garageImagePreview && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-2">Garage Photo</h4>
                  <img src={garageImagePreview} alt="Garage preview" className="w-32 h-24 object-cover rounded-lg" />
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Garage Partner Onboarding
          </h1>
          <p className="text-gray-600">
            Provide your parking details to join ParkRez and unlock new revenue streams.
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.key} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                      index <= currentStepIndex
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {index + 1}
                  </div>
                  <div className="mt-2 text-center">
                    <p className={`text-xs font-medium ${
                      index <= currentStepIndex ? 'text-primary-600' : 'text-gray-500'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-gray-400">{step.description}</p>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    index < currentStepIndex ? 'bg-primary-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="card">
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900">
              {steps[currentStepIndex].title}
            </h2>
            <p className="text-gray-600 mt-1">
              {steps[currentStepIndex].description}
            </p>
          </div>

          <form onSubmit={currentStep === 'review' ? undefined : handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStepIndex === 0}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex space-x-3">
                {currentStepIndex < steps.length - 1 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-primary"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <div className="flex items-center space-x-2">
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span>Submitting...</span>
                      </div>
                    ) : (
                      'Submit My Garage'
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage; 